import {
  getBffBaseUrl,
  getVisitKey,
  TODAY_CONTEXT_PATH,
  TODAY_DISMISS_PATH,
  TODAY_MODULE_PATH,
} from '@/lib/bff/config'

/*
 * BFF client for the Today dashboard (VIS-257).
 *
 * The BFF is the source of truth for the role-aware composition: it decides
 * which modules a user's role and licence entitle them to (returned as an
 * ordered list of module ids), whether they may read Today at all, and which
 * modules the tenant has dismissed for the current day. Each module's data is
 * fetched independently so one module failing does not block the others, and
 * dismissal is recorded server-side against the tenant + calendar day.
 *
 * The permission/role/licence model itself is assumed to already exist and be
 * queryable (see the story's assumptions); this client only consumes its
 * decisions. Copy for problem statements, action verbs and status is owned by
 * the UI i18n layer keyed off the structured `kind`/`status` fields below —
 * the BFF returns data, never rendered sentences.
 */

/** The modules the Today dashboard can compose, in no particular order here. */
export type ModuleId =
  | 'needs-attention'
  | 'tonight'
  | 'pre-orders'
  | 'numbers'
  | 'setup-progress'
  | 'portfolio'
  | 'recent-activity'

/** Whether the caller may see Today's modules, and by which route. */
export type TodayAccess = {
  /** Holds `today.view` in this tenant. */
  canRead: boolean
  /** Is in the Landlord group (ADR-051). */
  isLandlord: boolean
}

/** A venue the caller may scope Today to. The `all` sentinel means every venue. */
export type Venue = { id: string; name: string }

/** Page-level context that frames the modules. */
export type TodayContext = {
  access: TodayAccess
  /** Greeting name for the venue-scoped header, or null to fall back to a generic greeting. */
  greetingName: string | null
  /** Venues the caller may scope to; always includes the `all` sentinel first. */
  venues: Venue[]
  /** Composed, ordered module ids the caller's role/licence entitles them to. */
  modules: ModuleId[]
  /** Module ids the tenant has dismissed for the current calendar day. */
  dismissed: ModuleId[]
}

/** A single "needs attention" problem. Severity orders the list; occurredAt breaks ties (recency). */
export type NeedsAttentionKind =
  | 'failed-sends'
  | 'unsynced-menu'
  | 'orders-not-delivered'
  | 'expiring-memberships'

export type NeedsAttentionItem = {
  id: string
  kind: NeedsAttentionKind
  /** Affected count surfaced next to the problem statement. */
  count: number
  /** Higher is more severe; used as the primary sort key. */
  severity: number
  /** ISO timestamp; secondary sort key (more recent first). */
  occurredAt: string
  /** Whether the action resolves inline (e.g. Sync) or routes to filtered records (Review). */
  resolution: 'resolve' | 'review'
}

export type NeedsAttentionData = {
  /** All qualifying items; the UI shows the top five and an "and N more" row. */
  items: NeedsAttentionItem[]
}

/** A messaging channel that can be configured for a production. */
export type MessagingChannelId = 'email' | 'sms' | 'post-show'

/** Per-channel messaging outcome for a performance. */
export type MessagingChannel = {
  channel: MessagingChannelId
  /**
   * Whether the channel has reached its send point yet. When false — e.g. a
   * post-show channel before the performance has run — the UI shows an em dash
   * rather than a zero count.
   */
  due: boolean
  sent: number
  failed: number
}

export type TonightPerformance = {
  /** Performance id; used in the View route and per-channel failure links. */
  id: string
  /** Production id; used in the View route. */
  productionId: string
  /** Production name, e.g. "Hamlet". */
  production: string
  /** Venue name the performance runs at, e.g. "Main House". */
  venue: string
  /** ISO start time; cards are ordered by this. */
  startsAt: string
  /** Booked attendance count. */
  booked: number
  orders: number
  tables: number
  messaging: MessagingChannel[]
}

export type TonightData = {
  performances: TonightPerformance[]
  /** When there are no performances today, a link target to the next dated one. */
  nextDated: { id: string; title: string; date: string } | null
}

/** Pre-orders due soon, grouped by outlet. */
export type PreOrderGroup = { outlet: string; count: number }
export type PreOrdersData = { groups: PreOrderGroup[] }

/** The five headline figures. Always rendered. */
export type TodayNumbers = {
  visits: number
  orders: number
  revenue: number
  memberships: number
  tables: number
  /** ISO 4217 currency code for the revenue figure. */
  currency: string
}

export type SetupProgressData = {
  completed: number
  total: number
  /** The next outstanding setup topic to route to. */
  nextTopic: { id: string; name: string }
}

/** Another tenant, by name only: no status, licence or activity (VIS-326 AC 6). */
export type PortfolioTenant = {
  id: string
  name: string
}
export type PortfolioData = { tenants: PortfolioTenant[] }

export type ActivityEntry = {
  id: string
  /** Job label, e.g. "Nightly membership export". */
  job: string
  status: 'success' | 'failed' | 'running'
  /** ISO timestamp of the outcome. */
  at: string
}
export type RecentActivityData = { entries: ActivityEntry[] }

/** Maps each module id to the shape its data endpoint returns. */
export type TodayModuleDataMap = {
  'needs-attention': NeedsAttentionData
  tonight: TonightData
  'pre-orders': PreOrdersData
  numbers: TodayNumbers
  'setup-progress': SetupProgressData
  portfolio: PortfolioData
  'recent-activity': RecentActivityData
}

/** Raised when the BFF rejects the access token (HTTP 401). */
export class TodayAuthError extends Error {
  constructor(message = 'Not authorised for Today') {
    super(message)
    this.name = 'TodayAuthError'
  }
}

/** Raised for any other failure (network, timeout, 403, 5xx, malformed body); `status` is 0 when no response arrived. */
export class TodayRequestError extends Error {
  readonly status: number

  constructor(message = 'Could not load Today', status = 0) {
    super(message)
    this.name = 'TodayRequestError'
    this.status = status
  }
}

/** The `all venues` sentinel id shared by the client and the venue-scope store. */
export const ALL_VENUES = 'all'

function baseHeaders(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    'x-visit-key': getVisitKey(),
    Accept: 'application/json',
  }
}

function scopeQuery(tenantId: string, venueId: string): string {
  const params = new URLSearchParams({ tenant: tenantId, venue: venueId })
  return params.toString()
}

async function readJson(response: Response): Promise<unknown> {
  if (response.status === 401) throw new TodayAuthError()
  if (!response.ok) throw new TodayRequestError(`Today request failed (${response.status})`)
  try {
    return await response.json()
  } catch {
    throw new TodayRequestError('Malformed Today response')
  }
}

/*
 * Resolve the page-level Today context for a tenant + venue scope: the access
 * decision, the caller's venues, the composed module list, and the modules the
 * tenant has dismissed today.
 */
export async function fetchTodayContext(
  token: string,
  tenantId: string,
  venueId: string,
  signal?: AbortSignal,
): Promise<TodayContext> {
  const url = `${getBffBaseUrl('mmv')}${TODAY_CONTEXT_PATH}?${scopeQuery(tenantId, venueId)}`
  let response: Response
  try {
    response = await fetch(url, { method: 'GET', headers: baseHeaders(token), signal })
  } catch {
    throw new TodayRequestError('Network error while loading Today')
  }
  const data = await readJson(response)
  if (!data || typeof data !== 'object') throw new TodayRequestError('Unexpected Today context shape')
  return data as TodayContext
}

/*
 * Fetch one module's data. Kept independent per module so a single module's
 * failure surfaces its own inline error/retry without blocking the others.
 */
export async function fetchTodayModule<M extends ModuleId>(
  module: M,
  token: string,
  tenantId: string,
  venueId: string,
  signal?: AbortSignal,
): Promise<TodayModuleDataMap[M]> {
  const url = `${getBffBaseUrl('mmv')}${TODAY_MODULE_PATH}/${module}?${scopeQuery(tenantId, venueId)}`
  let response: Response
  try {
    response = await fetch(url, { method: 'GET', headers: baseHeaders(token), signal })
  } catch {
    throw new TodayRequestError('Network error while loading module')
  }
  const data = await readJson(response)
  if (!data || typeof data !== 'object') throw new TodayRequestError('Unexpected module shape')
  return data as TodayModuleDataMap[M]
}

/*
 * Record a module dismissal against the tenant for the current calendar day.
 * The dismissal applies to every user in the tenant until the day rolls over.
 */
export async function dismissTodayModule(
  token: string,
  tenantId: string,
  module: ModuleId,
): Promise<void> {
  const url = `${getBffBaseUrl('mmv')}${TODAY_DISMISS_PATH}`
  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { ...baseHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenant: tenantId, module }),
    })
  } catch {
    throw new TodayRequestError('Network error while dismissing module')
  }
  if (response.status === 401) throw new TodayAuthError()
  if (!response.ok) throw new TodayRequestError(`Dismissal failed (${response.status})`, response.status)
}
